import { downloadApi } from '../../api/download';
import type { DownloadTarget, ManifestFile } from './types';

/**
 * Supplies presigned URLs to the fetch pipeline, in batches, just in time.
 *
 * Presigned URLs expire; a 30 GB download over a weak connection outlives any
 * reasonable TTL. Rather than signing 5,000 URLs up front and watching them go
 * stale, we sign ~100 at a time and refill as the pipeline advances. A forced
 * refresh (used by the pipeline's last retry) re-signs a single photo, which is
 * what rescues a transfer that ran past the expiry.
 */

const BATCH_SIZE = 100;
/** Refill once the cache has fewer than this many unused URLs left. */
const REFILL_THRESHOLD = 30;
/** Re-sign a URL this long before it actually expires. */
const EXPIRY_MARGIN_MS = 5 * 60 * 1000;

export class UrlProvider {
  private cache = new Map<string, string>();
  private expiresAt = 0;
  private inFlight: Promise<void> | null = null;
  private cursor = 0;

  constructor(
    private readonly target: DownloadTarget,
    private readonly sessionToken: string,
    private readonly files: ManifestFile[],
  ) {}

  /** True when the current batch of URLs is close enough to expiry to distrust. */
  private get stale(): boolean {
    return Date.now() > this.expiresAt - EXPIRY_MARGIN_MS;
  }

  private async fetchBatch(files: ManifestFile[]): Promise<void> {
    if (files.length === 0) return;
    const { urls, expiresAt } = await downloadApi.getUrls(
      this.target,
      this.sessionToken,
      files.map((f) => ({ photoId: f.photoId, proof: f.proof })),
    );
    for (const [photoId, url] of Object.entries(urls)) this.cache.set(photoId, url);
    this.expiresAt = new Date(expiresAt).getTime();
  }

  /** Pull in the next slice of URLs, coalescing concurrent callers. */
  private async refill(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    const slice = this.files.slice(this.cursor, this.cursor + BATCH_SIZE);
    if (slice.length === 0) return;
    this.cursor += slice.length;
    this.inFlight = this.fetchBatch(slice).finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  async getUrl(file: ManifestFile, forceRefresh = false): Promise<string | null> {
    if (forceRefresh || this.stale) {
      // Re-sign just this photo. Cheap, and avoids invalidating a batch that is
      // still perfectly good for its other entries.
      await this.fetchBatch([file]);
      return this.cache.get(file.photoId) ?? null;
    }

    if (!this.cache.has(file.photoId)) {
      await this.refill();
      // A coalesced refill may have covered a different slice; ask directly.
      if (!this.cache.has(file.photoId)) await this.fetchBatch([file]);
    } else if (this.cursor - this.cache.size < REFILL_THRESHOLD) {
      // Warm the next slice in the background so the pipeline never stalls
      // waiting on a signing round-trip.
      void this.refill().catch(() => {});
    }

    return this.cache.get(file.photoId) ?? null;
  }

  /** Drop a consumed URL so the cache doesn't grow to the size of the album. */
  release(photoId: string): void {
    this.cache.delete(photoId);
  }
}
